import { ObjectId } from "@novorender/data-js-api";
import { createContext, Dispatch, MutableRefObject } from "react";

import { VecRGBA } from "utils/color";

import { toIdArr, toIdObj } from "../utils";

export enum HighlightCollection {
    SecondaryHighlight = "secondaryHighlight",
    FormsNew = "formsNew",
    FormsOngoing = "formsOngoing",
    FormsCompleted = "formsCompleted",
    FormsFinished = "formsFinished",
    ClashObjects1 = "clashObjects1",
    ClashObjects2 = "clashObjects2",
    /**
     * Transparent selection base is made of selected objects in transparent mode.
     * Whenever selected object in transparent mode is unselected - it disappears (because default highlight kicks in).
     * To avoid it - we instead add unselected objects into this collection.
     * Selection is not only remembered when we switch to transparent mode, but also when new selection is added e.g. via Model Tree.
     * NOTE: instead of adding to this collection on mode change - we could just add objects one by one when unselecting,
     * but we'll need to rebuild ID array more often (not sure if it's worse actually).
     */
    TransparentSelectionBase = "transparentSelectionBase",
}

export const highlightCollectionKeys = Object.values(HighlightCollection);

export const initialState = {
    [HighlightCollection.SecondaryHighlight]: {
        ids: {} as Record<ObjectId, true | undefined>,
        idArr: [] as ObjectId[],
        color: [1, 1, 0, 1] as VecRGBA,
    },
    [HighlightCollection.FormsNew]: {
        ids: {} as Record<ObjectId, true | undefined>,
        idArr: [] as ObjectId[],
        color: [0.5, 0, 0, 1] as VecRGBA,
    },
    [HighlightCollection.FormsOngoing]: {
        ids: {} as Record<ObjectId, true | undefined>,
        idArr: [] as ObjectId[],
        color: [1, 0.75, 0, 1] as VecRGBA,
    },
    [HighlightCollection.FormsCompleted]: {
        ids: {} as Record<ObjectId, true | undefined>,
        idArr: [] as ObjectId[],
        color: [0, 0, 1, 1] as VecRGBA,
    },
    [HighlightCollection.FormsFinished]: {
        ids: {} as Record<ObjectId, true | undefined>,
        idArr: [] as ObjectId[],
        color: [0, 0.5, 0, 1] as VecRGBA,
    },
    [HighlightCollection.ClashObjects1]: {
        ids: {} as Record<ObjectId, true | undefined>,
        idArr: [] as ObjectId[],
        color: [1, 0, 0, 1] as VecRGBA,
    },
    [HighlightCollection.ClashObjects2]: {
        ids: {} as Record<ObjectId, true | undefined>,
        idArr: [] as ObjectId[],
        color: [0, 0, 1, 1] as VecRGBA,
    },
    [HighlightCollection.TransparentSelectionBase]: {
        ids: {} as Record<ObjectId, true | undefined>,
        idArr: [] as ObjectId[],
        color: [0, 0, 0, 1] as VecRGBA,
    },
};

export type State = typeof initialState;
type Key = keyof State;

enum ActionTypes {
    Add,
    Move,
    Remove,
    SetIds,
    SetColor,
    Set,
    SetAll,
    ClearAll,
    ClearForms,
}

function add(collection: Key, ids: ObjectId[]) {
    return {
        type: ActionTypes.Add as const,
        collection,
        ids,
    };
}

function move(fromCollection: Key, toCollection: Key, ids: ObjectId[]) {
    return {
        type: ActionTypes.Move as const,
        fromCollection,
        toCollection,
        ids,
    };
}

function remove(collection: Key, ids: ObjectId[]) {
    return {
        type: ActionTypes.Remove as const,
        collection,
        ids,
    };
}

function setIds(collection: Key, ids: ObjectId[]) {
    return {
        type: ActionTypes.SetIds as const,
        collection,
        ids,
    };
}

function setColor(collection: Key, color: State[Key]["color"]) {
    return {
        type: ActionTypes.SetColor as const,
        collection,
        color,
    };
}

function set(collection: Key, payload: { color: State[Key]["color"]; ids: ObjectId[] }) {
    return {
        type: ActionTypes.Set as const,
        collection,
        payload,
    };
}

function setAll(payload: State) {
    return {
        type: ActionTypes.SetAll as const,
        payload,
    };
}

function clearAll() {
    return {
        type: ActionTypes.ClearAll as const,
    };
}

function clearForms() {
    return {
        type: ActionTypes.ClearForms as const,
    };
}

export const actions = {
    add,
    move,
    remove,
    setIds,
    setColor,
    set,
    setAll,
    clearAll,
    clearForms,
};

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchHighlightCollection = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyState>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchHighlightCollection>(undefined as any);

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Add: {
            const collection = state[action.collection];
            const ids = { ...collection.ids, ...toIdObj(action.ids) };

            return {
                ...state,
                [action.collection]: {
                    ...collection,
                    ids,
                    idArr: toIdArr(ids),
                },
            };
        }
        case ActionTypes.Move: {
            const fromCollection = state[action.fromCollection];

            action.ids.forEach((id) => {
                delete fromCollection.ids[id];
            });

            const toCollection = state[action.toCollection];
            const ids = { ...toCollection.ids, ...toIdObj(action.ids) };

            return {
                ...state,
                [action.fromCollection]: {
                    ...fromCollection,
                    idArr: toIdArr(fromCollection.ids),
                },
                [action.toCollection]: {
                    ...toCollection,
                    ids,
                    idArr: toIdArr(ids),
                },
            };
        }
        case ActionTypes.Remove: {
            const collection = state[action.collection];

            action.ids.forEach((id) => {
                delete collection.ids[id];
            });

            return {
                ...state,
                [action.collection]: {
                    ...collection,
                    idArr: toIdArr(collection.ids),
                },
            };
        }
        case ActionTypes.SetIds: {
            const collection = state[action.collection];

            return {
                ...state,
                [action.collection]: {
                    ...collection,
                    ids: toIdObj(action.ids),
                    idArr: action.ids,
                },
            };
        }
        case ActionTypes.SetColor: {
            const collection = state[action.collection];

            return {
                ...state,
                [action.collection]: {
                    ...collection,
                    color: action.color,
                },
            };
        }
        case ActionTypes.Set: {
            return {
                ...state,
                [action.collection]: {
                    color: action.payload.color,
                    idArr: action.payload.ids,
                    ids: toIdObj(action.payload.ids),
                },
            };
        }
        case ActionTypes.ClearAll: {
            return {
                ...state,
                [HighlightCollection.SecondaryHighlight]: {
                    color: state.secondaryHighlight.color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.FormsNew]: {
                    color: state.formsNew.color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.FormsOngoing]: {
                    color: state.formsOngoing.color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.FormsCompleted]: {
                    color: state.formsCompleted.color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.FormsFinished]: {
                    color: state.formsFinished.color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.ClashObjects1]: {
                    color: state[HighlightCollection.ClashObjects1].color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.ClashObjects2]: {
                    color: state[HighlightCollection.ClashObjects2].color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.TransparentSelectionBase]: {
                    color: state[HighlightCollection.TransparentSelectionBase].color,
                    idArr: [],
                    ids: {},
                },
            };
        }
        case ActionTypes.SetAll: {
            return action.payload;
        }
        case ActionTypes.ClearForms: {
            return {
                ...state,
                [HighlightCollection.FormsNew]: {
                    color: state.formsNew.color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.FormsOngoing]: {
                    color: state.formsOngoing.color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.FormsCompleted]: {
                    color: state.formsCompleted.color,
                    idArr: [],
                    ids: {},
                },
                [HighlightCollection.FormsFinished]: {
                    color: state.formsFinished.color,
                    idArr: [],
                    ids: {},
                },
            };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
